/**
 * The live call, owned once for the whole application.
 *
 * A call is a fact about the person, not about the tab they happen to be looking at.
 * It can start on their phone while they are reading an order, or from a queue row on
 * the website, and either way the write-up has to be in front of them without taking
 * them away from whatever they were doing. So this sits on the app shell, above the
 * router: it holds the call, the timer and the draft, and every surface that shows a
 * call — the dock on any page, the full panel on /sales-command-center — reads from
 * here rather than keeping its own copy.
 *
 * The one navigation rule, and the reason it exists:
 *
 *   dialled on the phone   the person is not looking at the browser, so the browser
 *                          comes to them — it opens /sales-command-center once
 *   dialled on the website they are already looking at the right thing; moving them
 *                          would interrupt the very click they just made, so nothing
 *                          navigates and the dock opens where they stand
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useOrgSession, useOpenCall, useCallStatus, useActiveRun, useBeginCall, useEndCall,
  useSaveDraft, useDispose, useStartRun,
  useRequestDial, useDialStatus, useCancelDial,
} from '../hooks/useMobileOrgApi';

const LiveCallContext = createContext(null);

const HOME = '/sales-command-center';
const DRAFT_DEBOUNCE_MS = 800;

const emptyFeedback = () => ({
  outcome: null,
  notes: '',
  nextAction: null,
  nextFollowUpAt: null,
  fields: {},
});

/** The console's shape of a record, from whatever the call was stamped with. */
const itemFromCall = (c) => ({
  module: c.module,
  recordId: c.recordId,
  key: `${c.module}:${c.recordId}`,
  company: c.snapshot?.company || null,
  contactPerson: c.snapshot?.contactPerson || null,
  phone: c.phoneDialled || c.snapshot?.phone || null,
  country: c.snapshot?.country || null,
  rfqNo: c.snapshot?.rfqNo || null,
});

export function LiveCallProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const session = useOrgSession();
  const ready = session.isSuccess;

  /*
   * Declared up here, ahead of its first reader, not beside the dial code it belongs
   * to: `const` is not hoisted, so leaving it further down threw on every render.
   *
   * It is set from the moment Ring my phone is pressed until the handset's call shows
   * up here — exactly the window in which the open-call poll is the thing the rep is
   * waiting on, which is why that poll reads it. See useOpenCall.
   */
  const [ringing, setRinging] = useState(null);

  const openCall = useOpenCall(ready, Boolean(ringing));
  const run = useActiveRun(ready);

  const begin = useBeginCall();
  const finish = useEndCall();
  const draft = useSaveDraft();
  const dispose = useDispose();
  const startRunM = useStartRun();

  const [item, setItem] = useState(null);
  const [call, setCall] = useState(null);
  const [seconds, setSeconds] = useState(0);
  /** idle → live (on the phone) → writing (hung up, finishing the form) */
  const [phase, setPhase] = useState('idle');
  const [feedback, setFeedback] = useState(emptyFeedback);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const cancelDial = useCancelDial();
  /** The dock can be shrunk to a pill; the call keeps running either way. */
  const [minimised, setMinimised] = useState(false);

  const tick = useRef(null);
  const debounce = useRef(null);
  const pending = useRef(null);
  /** Calls this browser placed — the ones that must NOT drag the user anywhere. */
  const localCalls = useRef(new Set());
  const adoptedId = useRef(null);
  const redirected = useRef(null);

  const stopTimer = useCallback(() => {
    if (tick.current) { clearInterval(tick.current); tick.current = null; }
  }, []);
  const stopDebounce = useCallback(() => {
    if (debounce.current) { clearTimeout(debounce.current); debounce.current = null; }
  }, []);

  useEffect(() => () => { stopTimer(); stopDebounce(); }, [stopTimer, stopDebounce]);

  const reset = useCallback(() => {
    stopTimer();
    stopDebounce();
    setItem(null);
    setCall(null);
    setSeconds(0);
    setPhase('idle');
    setFeedback(emptyFeedback());
    setSavedAt(null);
    setError(null);
    setMinimised(false);
    pending.current = null;
  }, [stopTimer, stopDebounce]);

  /** Puts a server call object on screen, whichever device created it. */
  const attach = useCallback((server) => {
    const started = new Date(server.startedAt).getTime();
    const ended = server.endedAt ? new Date(server.endedAt).getTime() : null;

    setItem(itemFromCall(server));
    setCall(server);
    setError(null);
    setMinimised(false);
    // Elapsed from startedAt, not from zero: the call is however old it is, regardless
    // of when this tab found out about it.
    setSeconds(Math.max(0, Math.round(((ended || Date.now()) - started) / 1000)));
    setFeedback({
      ...emptyFeedback(),
      outcome: server.draft?.outcome || null,
      notes: server.draft?.notes || '',
      nextAction: server.draft?.nextAction || null,
      nextFollowUpAt: server.draft?.nextFollowUpAt || null,
      fields: server.draft?.fields || {},
    });
    setSavedAt(server.draft?.updatedAt ? new Date(server.draft.updatedAt) : null);
    pending.current = null;

    stopTimer();
    if (ended) {
      setPhase('writing');
    } else {
      setPhase('live');
      tick.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
  }, [stopTimer]);

  /** Dialling from the website. Never navigates — the dock appears where you are. */
  const requestDial = useRequestDial();
  const dialStatus = useDialStatus(Boolean(ringing));

  /**
   * Ring the caller's own phone for this record.
   *
   * No call is created here on purpose. The phone creates it when it dials, and the
   * panel adopts it through the same polling that picks up a call started anywhere else
   * — creating one at both ends would leave two records of one conversation.
   */
  const ringPhone = useCallback(async (target) => {
    if (phase !== 'idle') return null;
    setError(null);
    setNotice(null);
    try {
      const res = await requestDial.mutateAsync(target);
      setRinging({ ...target, requestId: res?.requestId, at: Date.now() });
      setNotice('Asking your phone to dial…');
      return res;
    } catch (err) {
      setError(err);
      return null;
    }
  }, [phase, requestDial]);

  /**
   * Stop waiting for a phone that is not going to answer.
   *
   * A locked handset never claims the request, so the console sat on "ringing" until the
   * server's three-minute expiry — with every call button in the list disabled and, since
   * nothing rendered `notice`, nothing on screen explaining it. The rep's only option was
   * to wait it out or reload.
   */
  const cancelRing = useCallback(async () => {
    setRinging(null);
    setNotice(null);
    try {
      await cancelDial.mutateAsync();
    } catch {
      /* The server expires it on its own; clearing the console is what matters here. */
    }
  }, [cancelDial]);

  /* Stop watching once the phone has taken it, or the request has gone stale. */
  useEffect(() => {
    const state = dialStatus.data?.state;
    if (!ringing) return;
    if (state === 'ringing') setNotice('Your phone is dialling…');
    if (state === 'none' || state === 'expired' || phase !== 'idle') setRinging(null);
    if (state === 'expired') setNotice('Your phone did not pick that up. Is the app open?');
  }, [dialStatus.data, ringing, phase]);

  const startCall = useCallback(async (target) => {
    if (phase !== 'idle' || begin.isPending) return null;
    setNotice(null);
    setError(null);
    try {
      const created = await begin.mutateAsync({
        module: target.module,
        recordId: target.recordId,
        phone: target.phone,
      });
      const id = String(created._id || created.id);
      localCalls.current.add(id);
      adoptedId.current = id;
      redirected.current = id;      // already where they want to be

      attach(created);
      setItem((prev) => ({ ...prev, ...target, ...itemFromCall(created) }));

      if (target.phone) {
        // A synthetic anchor, not window.location: a browser with no `tel:` handler
        // treats an assignment as a navigation and would unload the page mid-call.
        const link = document.createElement('a');
        link.href = `tel:${String(target.phone).replace(/[^\d+]/g, '')}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      return created;
    } catch (err) {
      setError(err);
      return null;
    }
  }, [phase, begin, attach]);

  /**
   * Adopt whatever is already running, and bring the browser to it only when the call
   * came from somewhere else. Keyed on the call id so this happens once per call: a
   * caller who deliberately navigates away is not dragged back on the next poll.
   */
  useEffect(() => {
    const server = openCall.data;
    const serverId = server ? String(server._id || server.id) : null;

    if (serverId && phase === 'idle' && adoptedId.current !== serverId) {
      adoptedId.current = serverId;
      setNotice(null);
      attach(server);
    }

    /**
     * Bring the browser to the call only while it is actually connected.
     *
     * A call that has been hung up but not written up still needs finishing, and the
     * dock says so on whatever page you are on — but seizing the page for it would be
     * the same interruption this design exists to avoid. Someone hung up two minutes
     * ago and moved on; they can come back to it, or pick it up in Review.
     */
    if (
      serverId
      && !server.endedAt
      && redirected.current !== serverId
      && !localCalls.current.has(serverId)
    ) {
      redirected.current = serverId;
      if (location.pathname !== HOME) navigate(HOME);
    }
  }, [openCall.data, phase, attach, navigate, location.pathname]);

  /**
   * What happened to the call this tab is holding.
   *
   * Both devices can be showing it and either can finish it, so the one that did not
   * has to find out — otherwise it runs a timer for a call that is already written up
   * and asks for the same feedback a second time.
   */
  const heldId = call ? String(call._id || call.id) : null;
  const held = useCallStatus(heldId, phase !== 'idle');

  useEffect(() => {
    const server = held.data;
    if (!server || phase === 'idle') return;

    if (server.dispositioned) {
      adoptedId.current = null;
      localCalls.current.delete(String(server._id || server.id));
      reset();
      setNotice('That call was written up on your phone.');
      run.refetch();
      return;
    }

    // Hung up on the phone: the conversation is over, the write-up is not. Keep
    // whatever is already typed here rather than reseeding from the server draft.
    if (server.endedAt && phase === 'live') {
      stopTimer();
      setPhase('writing');
      if (server.durationSeconds) setSeconds(server.durationSeconds);
    }
  }, [held.data, phase, reset, run, stopTimer]);

  /**
   * Live feedback. Held locally straight away so typing never lags, pushed up on a
   * short debounce. A failed push is deliberately silent — a network blip must not
   * interrupt someone mid-conversation, and the disposition carries the values anyway.
   */
  const update = useCallback((patch) => {
    setFeedback((prev) => {
      const next = { ...prev, ...patch };
      pending.current = next;
      return next;
    });

    const id = call?._id || call?.id;
    if (!id) return;

    stopDebounce();
    debounce.current = setTimeout(() => {
      draft.mutate({ callId: id, ...pending.current }, { onSuccess: () => setSavedAt(new Date()) });
    }, DRAFT_DEBOUNCE_MS);
  }, [call, draft, stopDebounce]);

  /** They hung up. The clock stops; the write-up stays open until it is saved. */
  const hangUp = useCallback(async () => {
    stopTimer();
    stopDebounce();
    setPhase('writing');

    const id = call?._id || call?.id;
    if (!id) return;
    try {
      if (pending.current) await draft.mutateAsync({ callId: id, ...pending.current }).catch(() => {});
      await finish.mutateAsync({ callId: id, durationSeconds: seconds });
    } catch (err) {
      setError(err);
    }
  }, [call, seconds, finish, draft, stopTimer, stopDebounce]);

  /** Commits the write-up. Validation lives on the server, which owns the rules. */
  const save = useCallback(async () => {
    const id = call?._id || call?.id;
    if (!id) return false;
    setError(null);
    try {
      await dispose.mutateAsync({
        callId: id,
        outcome: feedback.outcome,
        notes: feedback.notes?.trim() || undefined,
        nextAction: feedback.nextAction || undefined,
        nextFollowUpAt: feedback.nextFollowUpAt || undefined,
        fields: Object.keys(feedback.fields || {}).length ? feedback.fields : undefined,
      });
      localCalls.current.delete(String(id));
      adoptedId.current = null;
      reset();
      setNotice('Saved to your day.');
      run.refetch();
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  }, [call, feedback, dispose, reset, run]);

  /** Opens a run and, on the website, shows it — without dialling anything yet. */
  const startRun = useCallback(async (options = {}) => {
    await startRunM.mutateAsync({ mode: 'auto', source: 'web', ...options });
    if (location.pathname !== HOME) navigate(HOME);
  }, [startRunM, navigate, location.pathname]);

  const value = useMemo(() => ({
    ready,
    session,
    run,
    item,
    call,
    seconds,
    phase,
    feedback,
    savedAt,
    error,
    notice,
    minimised,
    isLive: phase === 'live',
    isWriting: phase === 'writing',
    isBusy: phase !== 'idle',
    starting: begin.isPending,
    saving: dispose.isPending,
    startCall,
    ringPhone,
    ringing,
    cancelRing,
    dialState: dialStatus.data?.state || 'none',
    startRun,
    update,
    hangUp,
    save,
    setMinimised,
    dismiss: reset,
    clearError: () => setError(null),
    clearNotice: () => setNotice(null),
  }), [
    ready, session, run, item, call, seconds, phase, feedback, savedAt, error, notice,
    minimised, begin.isPending, dispose.isPending, startCall, ringPhone, ringing, cancelRing, dialStatus.data, startRun, update, hangUp,
    save, reset,
  ]);

  return <LiveCallContext.Provider value={value}>{children}</LiveCallContext.Provider>;
}

/** Null outside the provider, so a page can render without one rather than crashing. */
export const useLiveCall = () => useContext(LiveCallContext);

export default LiveCallProvider;
